import React, { useEffect, useState } from "react";
import CustomButton from "../../components/form/CustomButton";
import { useNavigate, useParams } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
    clientsResource,
    getPeriods,
    loadPackagesResource,
    plansResource,
    // TODO: descomentar cuando exista el endpoint
    // getUsersList,
} from "../../helpers/configRoutes/backend_helper";
import CustomInput from "../../components/form/CustomInput";
import { showFormErrors } from "../../helpers/showFormErrors";
import CustomSelect from "../../components/form/CustomSelect";
import CustomMultiSelect from "../../components/form/CustomMultiSelect";
import { currencyUSD } from "../../helpers/forms/formater";

/* ------------------------------------------------------------------ */
/* AI FEATURES                                                         */
/*  - key         → flag boolean en el form  (ai_chatbot, etc)         */
/*  - requestsKey → contador de solicitudes asociado                   */
/* ------------------------------------------------------------------ */
const AI_FEATURES = [
    {
        key: 'ai_chatbot',
        requestsKey: 'ai_chatbot_requests',
        name: 'Chatbot',
        description: 'AI-powered conversational assistant for customer support',
        icon: 'fas fa-comments',
    },
    {
        key: 'ai_load_creation',
        requestsKey: 'ai_load_creation_requests',
        name: 'Load creation from files',
        description: 'Auto-extract and create loads from uploaded documents',
        icon: 'fas fa-file-import',
    },
    {
        key: 'ai_truck_prediction',
        requestsKey: 'ai_truck_prediction_requests',
        name: 'Truck service prediction',
        description: 'Predictive maintenance alerts for your fleet',
        icon: 'fas fa-truck',
    },
];

const normalizeUnlimitedFlag = (value) => value === null || value === '' || value === undefined;

const formatPlanLimit = (value) => normalizeUnlimitedFlag(value) ? 'Unlimited' : value;

const nullableIntegerField = (label, unlimitedField) =>
    Yup.number()
        .transform((value, originalValue) => (originalValue === '' || originalValue == null ? null : value))
        .nullable()
        .when(unlimitedField, {
            is: false,
            then: (schema) => schema
                .typeError(`${label} must be a number`)
                .integer(`${label} must be an integer`)
                .min(0, `${label} cannot be negative`)
                .required(`${label} are required`),
            otherwise: (schema) => schema.nullable().notRequired(),
        });

const AddUpdPlan = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);

    const baseitem = {
        id: null,
        name: '',
        price: '',
        active: false,
        day_trial: '',
        trucks: '',
        users: '',
        trucks_unlimited: true,
        users_unlimited: true,
        period_id: '',
        taxes: 0,

        /* Load package incluido */
        packet_load_id: '',

        /* AI features (toggles + requests/mes por feature) */
        ai_chatbot: false,
        ai_chatbot_requests: '',
        ai_load_creation: false,
        ai_load_creation_requests: '',
        ai_truck_prediction: false,
        ai_truck_prediction_requests: '',

        /* Asignación a usuarios específicos (opcional) */
        assigned_user_ids: [],
    };

    const validation = useFormik({
        initialValues: baseitem,
        validationSchema: Yup.object({
            name: Yup.string().required('Name is required'),
            price: Yup.number().typeError('Price must be a number').min(0, 'Price cannot be negative').required('Price is required'),
            day_trial: Yup.number().typeError('Trial period must be a number').integer('Trial period must be an integer').min(0, 'Trial period cannot be negative').required('Trial period is required'),
            trucks: nullableIntegerField('Trucks', 'trucks_unlimited'),
            users: nullableIntegerField('Users', 'users_unlimited'),
            taxes: Yup.number().typeError('Taxes must be a number').min(0, 'Taxes cannot be negative').required('Taxes are required'),

            /* Si la feature está ON, su contador de requests pasa a ser obligatorio */
            ai_chatbot_requests: Yup.number()
                .when('ai_chatbot', {
                    is: true,
                    then: (s) =>
                        s.typeError('Must be a number')
                            .integer('Must be an integer')
                            .min(1, 'At least 1 request')
                            .required('Required when feature is enabled'),
                    otherwise: (s) => s.notRequired(),
                }),
            ai_load_creation_requests: Yup.number()
                .when('ai_load_creation', {
                    is: true,
                    then: (s) =>
                        s.typeError('Must be a number')
                            .integer('Must be an integer')
                            .min(1, 'At least 1 request')
                            .required('Required when feature is enabled'),
                    otherwise: (s) => s.notRequired(),
                }),
            ai_truck_prediction_requests: Yup.number()
                .when('ai_truck_prediction', {
                    is: true,
                    then: (s) =>
                        s.typeError('Must be a number')
                            .integer('Must be an integer')
                            .min(1, 'At least 1 request')
                            .required('Required when feature is enabled'),
                    otherwise: (s) => s.notRequired(),
                }),
        }),
        onSubmit: async (values, { setErrors }) => {
            setLoading(true);
            const payload = {
                ...values,
                users: values.users_unlimited ? null : values.users,
                trucks: values.trucks_unlimited ? null : values.trucks,
            };
            const response = await plansResource(values.id ? 'put' : 'post', payload, values.id);
            if (response.status) {
                setLoading(false);
                navigate('/subscriptions');
            } else {
                setErrors(response.errors || {});
            }
            setLoading(false);
        }
    });

    const [paymentPeriods, setPaymentPeriods] = useState([]);

    /* TODO: reemplazar mock por fetch real (p.ej. getLoadPackages()) cuando esté el endpoint */
    const [loadPackages, setLoadPackages] = useState([
        { id: 1, name: 'Starter — 10 loads/month' },
        { id: 2, name: 'Business — 25 loads/month' },
        { id: 3, name: 'Enterprise — 60 loads/month' },
    ]);

    /* TODO: reemplazar mock por getUsersList() cuando esté el endpoint */
    const [usersList, setUsersList] = useState([
        { id: 1, name: 'John Doe' },
        { id: 2, name: 'Jane Smith' },
        { id: 3, name: 'Carlos Pérez' },
        { id: 4, name: 'María Hernández' },
    ]);

    /* Carga del plan: si hay id en URL, hace fetch; si no, resetea al baseitem */
    useEffect(() => {
        if (!id) {
            validation.resetForm({ values: baseitem });
            return;
        }
        const fetchPlan = async () => {
            const response = await plansResource('show', null, id);
            if (response?.plan) {
                validation.setValues({
                    ...baseitem,
                    ...response.plan,
                    users_unlimited: normalizeUnlimitedFlag(response.plan.users),
                    trucks_unlimited: normalizeUnlimitedFlag(response.plan.trucks),
                });
            }
        };
        fetchPlan();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    useEffect(() => {
        const getCatalogs = async () => {
            const response = await getPeriods();
            setPaymentPeriods(response.periods);

            const responseClients = await clientsResource('get', { params: { select: true } })
            setUsersList(responseClients.companies)


            const responseLoadPackages = await loadPackagesResource('get', { params: { select: true } })
            setLoadPackages(responseLoadPackages.packages)


        };
        getCatalogs();
    }, []);

    const toggleAIFeature = (feature) => {
        const next = !validation.values[feature.key];
        validation.setFieldValue(feature.key, next);
        // Si se desactiva, limpia su contador
        if (!next) {
            validation.setFieldValue(feature.requestsKey, '');
        }
    };

    const toggleUnlimitedField = (fieldName, unlimitedField) => {
        const next = !validation.values[unlimitedField];
        validation.setFieldValue(unlimitedField, next);

        if (next) {
            validation.setFieldValue(fieldName, '');
        }
    };

    /* ---------- Valores calculados para el preview ---------- */
    const v = validation.values;
    const previewName = v.name?.trim() || 'Plan name';
    const previewPrice = v.price !== '' && v.price != null ? v.price : '0';
    const previewTaxesAmount = (parseFloat(v.price || 0) * parseFloat(v.taxes || 0)) / 100;
    const selectedLoadPackage = loadPackages.find(
        p => String(p.id) === String(v.packet_load_id)
    );
    const enabledAI = AI_FEATURES.filter(f => v[f.key]);
    const totalAIRequests = enabledAI.reduce(
        (acc, f) => acc + (parseInt(v[f.requestsKey]) || 0),
        0
    );
    const assignedUsers = usersList.filter(u =>
        v.assigned_user_ids?.includes(u.id)
    );

    return (
        <form
            onSubmit={e => {
                e.preventDefault();
                validation.handleSubmit();
                showFormErrors(validation);
            }}
            className="min-h-screen bg-cs-bg"
        >
            {/* ---------- TOP BAR (sticky) ---------- */}
            <div className="bg-cs-surface border-b border-cs-border px-8 py-4 sticky top-0 z-10">
                <div className="flex items-center justify-between gap-4">
                    <div className="flex items-center gap-3 min-w-0">
                        <button
                            type="button"
                            onClick={() => navigate('/subscriptions')}
                            className="h-9 w-9 rounded-md flex items-center justify-center text-cs-muted hover:bg-cs-bg-alt"
                            aria-label="Back"
                        >
                            <i className="fas fa-arrow-left" />
                        </button>

                        <div className="min-w-0">
                            <nav className="flex items-center gap-2 text-xs text-cs-muted">
                                <span>Subscriptions</span>
                                <i className="fas fa-chevron-right text-[8px]" />
                                <span className="text-cs-dark font-medium">
                                    {v.id ? 'Edit plan' : 'New plan'}
                                </span>
                            </nav>
                            <h2 className="text-xl font-semibold text-cs-dark truncate">
                                {v.id ? 'Edit Subscription Plan' : 'Create Subscription Plan'}
                            </h2>
                        </div>
                    </div>

                    <div className="flex items-center gap-3 shrink-0">
                        <CustomButton
                            type="button"
                            onClick={() => navigate('/subscriptions')}
                            className="bg-cs-bg-alt text-cs-dark"
                        >
                            Cancel
                        </CustomButton>

                        <CustomButton
                            loading={loading}
                            textLoad="Saving"
                            type="success"
                            className="px-8"
                        >
                            {v.id ? 'Save changes' : 'Create plan'}
                        </CustomButton>
                    </div>
                </div>
            </div>

            {/* ---------- BODY ---------- */}
            <div className="px-8 py-8 grid grid-cols-1 lg:grid-cols-3 gap-8">

                {/* -------- FORM (izquierda, 2/3) -------- */}
                <div className="lg:col-span-2 space-y-8">

                    {/* PLAN INFO */}
                    <section className="bg-cs-surface border border-cs-border rounded-xl p-6">
                        <h3 className="text-sm font-semibold text-cs-dark mb-4 uppercase tracking-wide">
                            Plan information
                        </h3>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <CustomInput
                                label="Subscription Name"
                                name="name"
                                error={validation.errors.name}
                                value={validation.values.name}
                                handleChange={validation.handleChange}
                            />

                            <CustomSelect
                                label="Payment Period"
                                name="period_id"
                                options={paymentPeriods}
                                error={validation.errors.period_id}
                                value={validation.values.period_id}
                                handleChange={validation.handleChange}
                            />
                        </div>
                    </section>

                    {/* PRICING */}
                    <section className="bg-cs-surface border border-cs-border rounded-xl p-6">
                        <h3 className="text-sm font-semibold text-cs-dark mb-4 uppercase tracking-wide">
                            Pricing
                        </h3>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <CustomInput
                                label="Price (USD)"
                                type="number"
                                min="0"
                                name="price"
                                error={validation.errors.price}
                                value={validation.values.price}
                                handleChange={validation.handleChange}
                            />

                            <CustomInput
                                label="Taxes %"
                                type="number"
                                min="0"
                                name="taxes"
                                error={validation.errors.taxes}
                                value={validation.values.taxes}
                                handleChange={validation.handleChange}
                            />

                            <CustomInput
                                label="Trial days"
                                type="number"
                                min="0"
                                name="day_trial"
                                error={validation.errors.day_trial}
                                value={validation.values.day_trial}
                                handleChange={validation.handleChange}
                            />
                        </div>
                    </section>

                    {/* USAGE LIMITS */}
                    <section className="bg-cs-surface border border-cs-border rounded-xl p-6">
                        <h3 className="text-sm font-semibold text-cs-dark mb-4 uppercase tracking-wide">
                            Usage limits
                        </h3>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {[
                                {
                                    label: 'Users',
                                    field: 'users',
                                    unlimitedField: 'users_unlimited',
                                    placeholder: 'Example: 10',
                                },
                                {
                                    label: 'Trucks',
                                    field: 'trucks',
                                    unlimitedField: 'trucks_unlimited',
                                    placeholder: 'Example: 25',
                                },
                            ].map(({ label, field, unlimitedField, placeholder }) => {
                                const isUnlimited = !!validation.values[unlimitedField];

                                return (
                                    <div key={field} className="rounded-xl border border-cs-border bg-cs-bg p-4">
                                        <div className="flex items-start justify-between gap-4">
                                            <div>
                                                <p className="text-sm font-medium text-cs-dark">{label}</p>
                                                <p className="mt-1 text-xs text-cs-muted">
                                                    {isUnlimited
                                                        ? `This plan can use unlimited ${label.toLowerCase()}.`
                                                        : `Set the maximum number of ${label.toLowerCase()} included in this plan.`}
                                                </p>
                                            </div>

                                            <label className="inline-flex items-center gap-2 text-sm font-medium text-cs-dark select-none">
                                                <input
                                                    type="checkbox"
                                                    checked={isUnlimited}
                                                    onChange={() => toggleUnlimitedField(field, unlimitedField)}
                                                    className="h-4 w-4 rounded border-cs-border text-cs-primary focus:ring-cs-primary/30"
                                                />
                                                Unlimited
                                            </label>
                                        </div>

                                        <div className="mt-4">
                                            <CustomInput
                                                label={`${label} limit`}
                                                type="number"
                                                min="0"
                                                name={field}
                                                error={validation.errors[field]}
                                                value={isUnlimited ? '' : validation.values[field]}
                                                handleChange={validation.handleChange}
                                                disabled={isUnlimited}
                                                placeholder={isUnlimited ? 'Unlimited' : placeholder}
                                                className={isUnlimited ? 'cursor-not-allowed opacity-60 bg-cs-bg-alt' : ''}
                                            />
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </section>

                    {/* INCLUDED JOB PACKAGE */}
                    <section className="bg-cs-surface border border-cs-border rounded-xl p-6">
                        <h3 className="text-sm font-semibold text-cs-dark mb-1 uppercase tracking-wide">
                            Included load package
                        </h3>
                        <p className="text-xs text-cs-muted mb-4">
                            Loads the customer receives per billing cycle. Leave empty for plans without included loads.
                        </p>

                        <CustomSelect
                            label="Load Package"
                            name="packet_load_id"
                            options={loadPackages}
                            error={validation.errors.packet_load_id}
                            value={validation.values.packet_load_id}
                            handleChange={validation.handleChange}
                        />
                    </section>

                    {/* AI FEATURES */}
                    <section className="bg-cs-surface border border-cs-border rounded-xl p-6">
                        <h3 className="text-sm font-semibold text-cs-dark mb-1 uppercase tracking-wide">
                            AI features
                        </h3>
                        <p className="text-xs text-cs-muted mb-4">
                            Activate features and set how many monthly requests are included for each one.
                        </p>

                        <div className="space-y-3">
                            {AI_FEATURES.map(feature => {
                                const enabled = !!validation.values[feature.key];
                                const requestsValue = validation.values[feature.requestsKey];
                                const requestsError = validation.errors[feature.requestsKey];

                                return (
                                    <div
                                        key={feature.key}
                                        className={`
                                            rounded-xl border transition
                                            ${enabled
                                                ? 'border-cs-primary bg-cs-primary-soft'
                                                : 'border-cs-border bg-cs-surface hover:border-cs-muted'}
                                        `}
                                    >
                                        {/* Cabecera de la feature (clickable para toggle) */}
                                        <button
                                            type="button"
                                            onClick={() => toggleAIFeature(feature)}
                                            className="w-full text-left p-4"
                                        >
                                            <div className="flex items-center gap-4">
                                                <div className={`
                                                    h-10 w-10 rounded-lg flex items-center justify-center shrink-0
                                                    ${enabled
                                                        ? 'bg-cs-primary text-white'
                                                        : 'bg-cs-bg-alt text-cs-muted'}
                                                `}>
                                                    <i className={`${feature.icon} text-base`} />
                                                </div>

                                                <div className="flex-1 min-w-0">
                                                    <h4 className="text-sm font-semibold text-cs-dark">
                                                        {feature.name}
                                                    </h4>
                                                    <p className="text-xs text-cs-muted leading-snug mt-0.5">
                                                        {feature.description}
                                                    </p>
                                                </div>

                                                <span className={`
                                                    relative inline-block h-5 w-9 rounded-full transition shrink-0
                                                    ${enabled ? 'bg-cs-primary' : 'bg-cs-border'}
                                                `}>
                                                    <span className={`
                                                        absolute top-0.5 h-4 w-4 rounded-full bg-white shadow transition-all
                                                        ${enabled ? 'left-[18px]' : 'left-0.5'}
                                                    `} />
                                                </span>
                                            </div>
                                        </button>

                                        {/* Input de requests — solo visible cuando la feature está ON */}
                                        {enabled && (
                                            <div className="px-4 pb-4 -mt-1">
                                                <div className="bg-cs-surface rounded-lg border border-cs-border/60 p-3 flex items-center gap-3">
                                                    <i className="fas fa-bolt text-cs-primary-dark text-xs shrink-0" />
                                                    <label
                                                        htmlFor={feature.requestsKey}
                                                        className="text-xs text-cs-dark whitespace-nowrap"
                                                    >
                                                        Monthly requests
                                                    </label>
                                                    <input
                                                        id={feature.requestsKey}
                                                        type="number"
                                                        min="1"
                                                        name={feature.requestsKey}
                                                        value={requestsValue}
                                                        onChange={validation.handleChange}
                                                        placeholder="e.g. 1000"
                                                        className={`
                                                            flex-1 h-9 px-3 rounded-md bg-cs-surface text-cs-dark
                                                            border text-sm
                                                            focus:outline-none focus:ring-2
                                                            ${requestsError
                                                                ? 'border-cs-danger focus:ring-cs-danger/30'
                                                                : 'border-cs-border focus:ring-cs-primary/30'}
                                                        `}
                                                    />
                                                </div>
                                                {requestsError && (
                                                    <p className="mt-1 text-xs text-cs-danger pl-1">
                                                        {requestsError}
                                                    </p>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </section>

                    {/* ── NUEVO: ASSIGN TO USERS ─────────────────────────────── */}
                    <section className="bg-cs-surface border border-cs-border rounded-xl p-6">
                        <h3 className="text-sm font-semibold text-cs-dark mb-1 uppercase tracking-wide">
                            Assign to users
                        </h3>
                        <p className="text-xs text-cs-muted mb-4">
                            Optional. Restrict this plan to specific users. Leave empty to make it available to everyone.
                        </p>

                        <CustomMultiSelect
                            label="Users"
                            name="assigned_user_ids"
                            placeholder="Select one or more users…"
                            options={usersList}
                            value={validation.values.assigned_user_ids}
                            setFieldValue={validation.setFieldValue}
                            error={validation.errors.assigned_user_ids}
                        />
                    </section>
                </div>

                {/* -------- LIVE PREVIEW (derecha, 1/3, sticky) -------- */}
                <aside className="lg:col-span-1">
                    <div className="lg:sticky lg:top-28">
                        <div className="flex items-center justify-between mb-3">
                            <h3 className="text-sm font-semibold text-cs-dark uppercase tracking-wide">
                                Preview
                            </h3>
                            <span className="text-xs text-cs-muted">Live</span>
                        </div>

                        {/* Card — idéntica a la del listing */}
                        <div className="relative rounded-xl bg-cs-surface border border-cs-border shadow-sm p-6">

                            {/* Badge load package */}
                            {selectedLoadPackage && (
                                <div className="mb-2">
                                    <span className="inline-flex items-center gap-1 rounded-full bg-cs-primary-soft text-cs-primary-dark text-xs font-medium px-2 py-0.5">
                                        <i className="fas fa-truck-loading" />
                                        {selectedLoadPackage.name}
                                    </span>
                                </div>
                            )}

                            {/* Name */}
                            <h3 className="text-lg font-semibold text-cs-dark">
                                {previewName}
                            </h3>

                            {/* Price */}
                            <div className="mt-4">
                                <span className="text-4xl font-bold text-cs-primary">
                                    ${previewPrice}
                                </span>
                                <span className="ml-1 text-sm text-cs-muted">/ month</span>
                            </div>

                            {/* Details */}
                            <div className="mt-6 space-y-2 text-sm text-cs-dark">
                                <div className="flex justify-between">
                                    <span>Taxes</span>
                                    <span>{currencyUSD(previewTaxesAmount)}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span>Trucks</span>
                                    <span>{formatPlanLimit(v.trucks_unlimited ? null : v.trucks)}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span>Users</span>
                                    <span>{formatPlanLimit(v.users_unlimited ? null : v.users)}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span>Trial</span>
                                    <span>{v.day_trial ? `${v.day_trial} days` : '—'}</span>
                                </div>
                                {totalAIRequests > 0 && (
                                    <div className="flex justify-between">
                                        <span>Total AI requests</span>
                                        <span className="font-semibold">
                                            {totalAIRequests.toLocaleString()}/mo
                                        </span>
                                    </div>
                                )}
                            </div>

                            {/* AI features chips — ahora con su contador de requests */}
                            {enabledAI.length > 0 && (
                                <div className="mt-5 pt-5 border-t border-cs-border">
                                    <p className="text-xs font-semibold text-cs-muted uppercase tracking-wide mb-2">
                                        AI included
                                    </p>
                                    <div className="flex flex-wrap gap-2">
                                        {enabledAI.map(f => {
                                            const reqs = parseInt(v[f.requestsKey]) || 0;
                                            return (
                                                <span
                                                    key={f.key}
                                                    className="inline-flex items-center gap-1.5 rounded-full bg-cs-primary-soft text-cs-primary-dark text-xs font-medium px-2.5 py-1"
                                                >
                                                    <i className={`${f.icon} text-[10px]`} />
                                                    {f.name}
                                                    {reqs > 0 && (
                                                        <span className="ml-1 text-[10px] font-semibold opacity-80">
                                                            · {reqs.toLocaleString()}/mo
                                                        </span>
                                                    )}
                                                </span>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}

                            {/* Usuarios asignados */}
                            {assignedUsers.length > 0 && (
                                <div className="mt-5 pt-5 border-t border-cs-border">
                                    <p className="text-xs font-semibold text-cs-muted uppercase tracking-wide mb-2">
                                        Assigned to ({assignedUsers.length})
                                    </p>
                                    <div className="flex flex-wrap gap-1.5">
                                        {assignedUsers.slice(0, 6).map(u => (
                                            <span
                                                key={u.id}
                                                className="inline-flex items-center gap-1 rounded-full bg-cs-bg-alt text-cs-dark text-[11px] px-2 py-0.5"
                                            >
                                                <i className="fas fa-user text-[9px] text-cs-muted" />
                                                {u.name}
                                            </span>
                                        ))}
                                        {assignedUsers.length > 6 && (
                                            <span className="inline-flex items-center rounded-full bg-cs-bg-alt text-cs-muted text-[11px] px-2 py-0.5">
                                                +{assignedUsers.length - 6} more
                                            </span>
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* Footer button — solo visual */}
                            <div className="mt-6">
                                <div className="w-full rounded-full py-2 text-center text-sm font-medium bg-cs-surface border border-cs-primary text-cs-primary">
                                    Activate
                                </div>
                            </div>
                        </div>

                        <p className="text-xs text-cs-muted mt-3 text-center">
                            This is how customers will see the plan
                        </p>
                    </div>
                </aside>
            </div>
        </form>
    );
};

export default AddUpdPlan;
